import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  gridFilteredSortedRowIdsSelector,
  Toolbar,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { useState } from 'react';

import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

interface CustomToolbarProps {
  isLoadingBills: boolean;
  showIsLoadingLineItems: boolean;
  isExporting: boolean;
  setIsExporting: (v: boolean) => void;
}

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides extends CustomToolbarProps {}
}

const BillReportsToolbar = ({
  isLoadingBills,
  showIsLoadingLineItems,
  isExporting,
  setIsExporting,
}: CustomToolbarProps) => {
  const snackbarHandler = useSnackbarHandler();
  const gridApiRef = useGridApiContext();
  const filteredRowIds = useGridSelector(
    gridApiRef,
    gridFilteredSortedRowIdsSelector,
  );
  const hasFilteredRows = filteredRowIds.length > 0;

  const [expandAll, setExpandAll] = useState<boolean>(false);

  const handleExpandAll = () => {
    const newVal = !expandAll;

    setExpandAll(newVal);
    if (newVal) {
      gridApiRef.current?.expandAllRows();
    } else {
      gridApiRef.current.collapseAllRows();
    }
  };

  const handleExport =
    (options = {}) =>
    () => {
      gridApiRef.current.exportDataAsCsv({
        ...options,
        fileName: `${new Date().toLocaleDateString()}-bills-report`,
      });
      snackbarHandler.success('Bills report exported.');
      setIsExporting(false);
    };

  if (isLoadingBills || showIsLoadingLineItems) {
    return (
      <Toolbar style={{ minHeight: 40, padding: '0 8px' }}>
        <Stack direction="row" alignItems="center" spacing={1} mr={1}>
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
          <Typography variant="body2">
            {isLoadingBills
              ? 'Loading bills and line items...'
              : 'Showing bills, loading line items...'}
          </Typography>
        </Stack>
      </Toolbar>
    );
  }

  return (
    <Toolbar style={{ minHeight: 40, padding: '0 8px' }}>
      <FormControlLabel
        disabled={isExporting || !hasFilteredRows}
        control={<Checkbox checked={expandAll} onClick={handleExpandAll} />}
        label="Expand All"
        slotProps={{ typography: { variant: 'body2' } }}
      />
      <Button
        disabled={isExporting || !hasFilteredRows}
        onClick={() => {
          setIsExporting(true);
          setTimeout(handleExport(), 250);
        }}
        color="primary"
        startIcon={<FileDownloadOutlinedIcon />}
        sx={{ fontSize: 13 }}
      >
        Export
      </Button>
    </Toolbar>
  );
};

export default BillReportsToolbar;
